import {
  initialStandardEntityState,
  StandardEntityState,
} from '@app/shared/store/standard-entity.state';
import { Role } from '@cco/model';
import {
  AddRole,
  AddRoleFailure,
  AddRoleSuccess,
  DeleteRole,
  DeleteRoleFailure,
  DeleteRoleSuccess,
  GetRole,
  GetRoleFailure,
  GetRoles,
  GetRolesFailure,
  GetRolesSuccess,
  GetRoleSuccess,
  RoleActions,
  UpdateRole,
  UpdateRoleFailure,
  UpdateRoleSuccess,
} from '@core/auth/actions/role.actions';
import { createEntityAdapter, EntityAdapter } from '@ngrx/entity';
import { DateTime } from 'luxon';

export type State = StandardEntityState<Role>;

export function sortRolesByName(a: Role, b: Role): number {
  return a.name.localeCompare(b.name);
}

export const adapter: EntityAdapter<Role> = createEntityAdapter<Role>({
  sortComparer: sortRolesByName,
});

export const initialRoleState: State = adapter.getInitialState({
  ...initialStandardEntityState,
});

export function reducer(state = initialRoleState, action: typeof RoleActions): State {
  switch (action.type) {
    case AddRole.type: {
      return { ...state, errors: { ...state.errors, ADD_MODEL_OBJECT: null }, adding: true };
    }

    case AddRoleSuccess.type: {
      return adapter.addOne(action.role, {
        ...state,
        errors: { ...state.errors, ADD_MODEL_OBJECT: null },
        adding: false,
      });
    }

    case AddRoleFailure.type: {
      return {
        ...state,
        errors: { ...state.errors, ADD_MODEL_OBJECT: action.error },
        adding: false,
      };
    }

    case GetRole.type: {
      return { ...state, errors: { ...state.errors, GET_MODEL_OBJECT: null } };
    }

    case GetRoleSuccess.type: {
      return adapter.upsertOne(action.role, {
        ...state,
        errors: { ...state.errors, GET_MODEL_OBJECT: null },
      });
    }

    case GetRoleFailure.type: {
      return {
        ...state,
        errors: { ...state.errors, GET_MODEL_OBJECT: action.error },
      };
    }

    case GetRoles.type: {
      return {
        ...state,
        errors: { ...state.errors, GET_MODEL_OBJECTS: null },
        loadingAllAt: DateTime.local().toISO(),
      };
    }

    case GetRolesSuccess.type: {
      return adapter.setAll(action.roles, {
        ...state,
        errors: { ...state.errors, GET_MODEL_OBJECTS: null },
        loadedAllAt: DateTime.local().toISO(),
        loadedAll: true,
      });
    }

    case GetRolesFailure.type: {
      return {
        ...state,
        errors: { ...state.errors, GET_MODEL_OBJECTS: action.error },
        loadingAllAt: null,
      };
    }

    case UpdateRole.type: {
      return { ...state, errors: { ...state.errors, UPDATE_MODEL_OBJECT: null }, updating: true };
    }

    case UpdateRoleSuccess.type: {
      return adapter.updateOne(
        { id: action.role.id, changes: action.role },
        {
          ...state,
          errors: { ...state.errors, UPDATE_MODEL_OBJECT: null },
          updating: false,
        },
      );
    }

    case UpdateRoleFailure.type: {
      return {
        ...state,
        errors: { ...state.errors, UPDATE_MODEL_OBJECT: action.error },
        updating: false,
      };
    }

    case DeleteRole.type: {
      return { ...state, errors: { ...state.errors, DELETE_MODEL_OBJECT: null }, deleting: true };
    }

    case DeleteRoleSuccess.type: {
      return adapter.removeOne(action.id as number, {
        ...state,
        errors: { ...state.errors, DELETE_MODEL_OBJECT: null },
        deleting: false,
      });
    }

    case DeleteRoleFailure.type: {
      return {
        ...state,
        errors: { ...state.errors, DELETE_MODEL_OBJECT: action.error },
        deleting: false,
      };
    }

    default:
      return state;
  }
}

export const { selectIds, selectEntities, selectAll, selectTotal } = adapter.getSelectors();
